import type { ListBuilderConfiguration } from '../types';
import {
	createClientNameColumn,
	createLocationColumn,
	createSalespeopleColumn,
} from './client/columns';
import { DEFAULT_LIST_REQUIRED_FIELDS } from './client/shared';

/**
 * PD-14228 — Ownership Client list configuration. Shows the clients that belong
 * to an ownership (the Clients tab of the ownership detail page). Data is served
 * by ClientsService.getAll() with an `ownershipId` filter, so it REUSES the main
 * client list's shared column factories (name + address, lead/cust + business
 * type, salesperson + alternate) to stay visually identical to the main Clients
 * list. The only differences are the actions column (remove from ownership) and
 * the bulk action template.
 */
export const OWNERSHIP_CLIENT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-ownership-clients-all',
			cacheTTL: 600000,
			name: 'All Ownership Clients',
			type: 'default',
			active: true,
			bulkActionTemplate: 'ownershipClients',
			// Users without clients:detail:any only see their own book of clients.
			// Lock the salespeople filter to "Me" so the server-enforced filter
			// can match what the UI shows (mirrors price_contract_client).
			filterRules: {
				salespeople: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
				},
			},
			columns: [
				createClientNameColumn(),
				createLocationColumn(),
				createSalespeopleColumn(),
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'delete-action',
									label: 'Remove from Ownership',
									iconName: 'trash',
									callbackName: 'removeFromOwnership',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 60,
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					widthConfig: {
						mode: 'fixed',
					},
				},
			],
			defaultSortId: 'column-client-name-asc',
			// Same fields the main Clients list selects, so the shared columns render
			// identically (status dot, city/state/zip line, badges, salesperson).
			requiredFields: DEFAULT_LIST_REQUIRED_FIELDS,
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'clientSearch',
			'leadOrCustomer',
			'businessTypes',
			'groups',
			'geography',
			'salesStatus',
			'salespeople',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'externalId', fieldLabel: 'ERP #' },
				{ fieldName: 'address1', fieldLabel: 'Address' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead/Cust' },
				{ fieldName: 'businessType', fieldLabel: 'Business Type' },
			],
		},
	],
};
